/** Keep default-product review UI aligned with Horizon's selected variant. */
import { ThemeEvents } from '@theme/events';

function initDefaultProduct(root) {
  if (root.dataset.faDefaultReady) return;
  root.dataset.faDefaultReady = 'true';
  const section = root.closest('.shopify-section');
  const reviews = root.querySelector('.fa-default-reviews');
  const stars = root.querySelector('.fa-product-title-strip__stars');
  let controller;
  let revision = 0;

  const updateVisibility = () => {
    if (reviews) reviews.hidden = !reviews.querySelector('.rh-review-card');
    if (stars) stars.hidden = !stars.querySelector('[data-review-hub-badge]');
  };
  const observer = new MutationObserver(updateVisibility);
  observer.observe(root, { childList: true, subtree: true });
  updateVisibility();

  const paramsFor = (sku, variantId) => new URLSearchParams({
    sku: sku || '',
    locale: root.dataset.locale || 'de',
    locale_root: root.dataset.localeRoot || '/',
    page_type: 'product',
    page_url: root.dataset.pageUrl,
    page_product_id: root.dataset.productId,
    product_id: root.dataset.productId,
    variant_id: String(variantId || ''),
    source_mode: 'product',
    limit: '20',
  });

  section?.addEventListener(ThemeEvents.variantUpdate, async (event) => {
    const data = event.detail?.data;
    if (!data || String(data.productId) !== root.dataset.productId) return;
    // The installed tier-price app uses the older event name. Run after
    // Horizon's synchronous listeners have updated the native variant input.
    queueMicrotask(() => document.dispatchEvent(new CustomEvent('variant:change')));
    const variant = event.detail.resource;
    const nextRoot = data.html?.querySelector('[data-fa-default-product]');
    const sku = nextRoot?.dataset.reviewSku ?? variant?.sku ?? '';
    const variantId = variant?.id || '';
    // Product information can update without changing the review identity.
    if (root.dataset.reviewSku === sku && root.dataset.variantId === String(variantId)) return;
    root.dataset.reviewSku = sku;
    root.dataset.variantId = String(variantId);
    controller?.abort();
    controller = new AbortController();
    const currentRevision = ++revision;
    // Detach the initial snippet targets too: their in-flight requests must not
    // overwrite reviews for a newly selected variant when they finish later.
    const resetTarget = (parent, selector, attribute) => {
      if (!parent) return null;
      const previous = parent.querySelector(selector);
      const next = document.createElement('div');
      next.setAttribute(attribute, '');
      if (previous) previous.replaceWith(next);
      else parent.append(next);
      return next;
    };
    const badge = resetTarget(stars, '[data-review-hub-badge-container]', 'data-review-hub-badge-container');
    const wall = resetTarget(reviews, '[data-review-hub-all-container]', 'data-review-hub-all-container');
    updateVisibility();

    const load = async (target, endpoint) => {
      if (!target) return;
      if (endpoint === 'badge' && !sku) return;
      try {
        const response = await fetch(`/apps/review-hub/${endpoint}?${paramsFor(sku, variantId)}`, {
          credentials: 'same-origin', signal: controller.signal,
        });
        if (!response.ok) throw new Error('Review service unavailable');
        const html = await response.text();
        if (currentRevision !== revision || !root.isConnected) return;
        target.innerHTML = html;
        window.reviewHubBindBadgeScroll?.(root);
        window.reviewHubInitTrustWall?.(root);
        window.reviewHubInitReviewModal?.(root);
        window.reviewHubSchema?.refresh();
        updateVisibility();
      } catch (error) {
        if (error.name !== 'AbortError' && currentRevision === revision) target.replaceChildren();
      }
    };
    await Promise.all([load(badge, 'badge'), load(wall, 'all')]);
  });
  const unload = (event) => {
    if (event.target !== section) return;
    controller?.abort();
    observer.disconnect();
    document.removeEventListener('shopify:section:unload', unload);
  };
  document.addEventListener('shopify:section:unload', unload);
}

document.querySelectorAll('[data-fa-default-product]').forEach(initDefaultProduct);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-fa-default-product]').forEach(initDefaultProduct);
});
